/**
 * Scheduled "optimize & apply": pure helpers for the schedule dialog and
 * list. Times are site wall-clock time in the site's IANA timezone (from
 * the site record via GraphQL), never the browser's.
 */

export const SCHEDULE_REPEAT_OPTIONS = [
  { value: 'once', label: 'Once' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

// Optimize steps a schedule runs and applies. Segmentation is never one.
export const SCHEDULE_STEP_OPTIONS = [
  { value: 'topology', label: 'Topology (parent moves)' },
  { value: 'power', label: 'Power plan' },
  { value: 'channel', label: 'Channels' },
];

export const SCHEDULE_STATUS_META = {
  running: { label: 'Running', color: 'info' },
  applied: { label: 'Applied', color: 'success' },
  started: { label: 'Started', color: 'success' },
  no_actions: { label: 'No changes', color: 'default' },
  skipped_busy: { label: 'Skipped (site busy)', color: 'warning' },
  missed: { label: 'Missed', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
};

/** Plain description of a scheduled run's phase. */
export function describeRunPhase(phase) {
  if (phase === 'applying') return 'applying its plan';
  if (phase === 'starting_iperf') return 'starting its iperf test';
  if (phase === 'starting_scan') return 'starting its neighbours scan';
  if (phase === 'measuring') return 'running its iperf test before optimizing';
  if (phase === 'stopping') return 'timed out and still stopping (nothing will be applied)';
  return 'optimizing';
}

/** True when tz is an IANA timezone this browser knows. */
export function isValidTimeZone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * The user's site(s) from a get_sites result. A single site is the user's
 * site, whatever its project_id. Users allowed on several projects get
 * every project's sites, each tagged with project_id: only those of the
 * current NMS project are kept (plus untagged ones, which come from a
 * single-project call). Never another project's site.
 */
export function sitesForProject(sites, projectId) {
  const list = (Array.isArray(sites) ? sites : []).filter(Boolean);
  if (list.length === 1) return list;
  if (!projectId) return list.filter((site) => !site.project_id);
  return list.filter((site) => !site.project_id || site.project_id === projectId);
}

/** Unique valid timezones from GraphQL site records, first seen first. */
export function siteTimezonesFromSites(sites) {
  const out = [];
  (Array.isArray(sites) ? sites : []).forEach((site) => {
    const tz = String(site?.timezone || '').trim();
    if (isValidTimeZone(tz) && !out.includes(tz)) out.push(tz);
  });
  return out;
}

/** Wall-clock parts of `date` in `tz`. */
function partsInZone(date, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

const pad = (n) => String(n).padStart(2, '0');

/** Tomorrow at hour:00 in the site's timezone, as "YYYY-MM-DDTHH:MM". */
export function defaultStartLocal(tz, hour = 2, now = new Date()) {
  const p = partsInZone(now, isValidTimeZone(tz) ? tz : 'UTC');
  // Calendar arithmetic on the site date, independent of the browser zone.
  const tomorrow = new Date(Date.UTC(p.year, p.month - 1, p.day + 1));
  return `${tomorrow.getUTCFullYear()}-${pad(tomorrow.getUTCMonth() + 1)}-${pad(tomorrow.getUTCDate())}T${pad(hour)}:00`;
}

/** Format an instant (ISO string or Date) in the site's timezone. */
export function formatInTimeZone(value, tz) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    timeZone: isValidTimeZone(tz) ? tz : undefined,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/** Plain-language repeat, e.g. "Weekly on Tuesday at 02:00". */
export function describeRepeat(repeat, startLocal) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(startLocal || ''));
  if (!m) return SCHEDULE_REPEAT_OPTIONS.find((o) => o.value === repeat)?.label || repeat || '';
  const [, y, mo, d, hh, mm] = m;
  const time = `${hh}:${mm}`;
  const weekday = WEEKDAYS[new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d))).getUTCDay()];
  switch (repeat) {
    case 'once': return `Once on ${y}-${mo}-${d} at ${time}`;
    case 'daily': return `Daily at ${time}`;
    case 'weekly': return `Weekly on ${weekday} at ${time}`;
    case 'biweekly': return `Every 2 weeks on ${weekday} at ${time}`;
    case 'monthly': return `Monthly on the ${ordinal(Number(d))} at ${time}`;
    default: return `${repeat} at ${time}`;
  }
}

/** Short scope summary of stored optimize settings. */
export function describeOptimizeScope(optimizeRequest) {
  const r = optimizeRequest || {};
  if (Array.isArray(r.custom_tree_ids) && r.custom_tree_ids.length > 0) return `${r.custom_tree_ids.length} custom trees`;
  if (r.custom_tree_id) return '1 custom tree';
  if (Array.isArray(r.cap_serials) && r.cap_serials.length > 0) return `${r.cap_serials.length} CAP${r.cap_serials.length === 1 ? '' : 's'}`;
  if (Array.isArray(r.focus_serials) && r.focus_serials.length > 0) return `${r.focus_serials.length} selected devices`;
  return 'Whole site';
}
